/**
 * Table Explorer MCP Command Store
 *
 * Holds pending MCP commands for table explorer tabs.
 * The TableExplorerTab component subscribes to this store and executes commands.
 */

import { create } from 'zustand';
import type { ChartConfig } from '../../charts/types/chartTypes';

export type RowDetailsLayout = 'bottom' | 'side';

export type TableExplorerMcpCommand =
  | { type: 'refresh' }
  | { type: 'show_chart'; show: boolean }
  | { type: 'set_chart_config'; config: ChartConfig }
  | { type: 'select_row'; rowIndex: number }
  | { type: 'show_row_details'; show: boolean; layout?: RowDetailsLayout; rowIndex?: number };

type PendingCommand = {
  id: string;
  tabId: string;
  command: TableExplorerMcpCommand;
};

type TableExplorerMcpState = {
  /** Pending commands for table explorer tabs */
  pendingCommands: PendingCommand[];
  /** Queue a command for a specific tab */
  queueCommand: (tabId: string, command: TableExplorerMcpCommand) => string;
  /** Get pending commands for a specific tab */
  getPendingCommands: (tabId: string) => PendingCommand[];
  /** Mark a command as completed */
  completeCommand: (commandId: string) => void;
  /** Clear all pending commands for a tab */
  clearTabCommands: (tabId: string) => void;
};

let commandIdCounter = 0;

export const useTableExplorerMcpStore = create<TableExplorerMcpState>((set, get) => ({
  pendingCommands: [],

  queueCommand: (tabId: string, command: TableExplorerMcpCommand): string => {
    const id = `mcp-cmd-${++commandIdCounter}`;
    set((state) => ({
      pendingCommands: [...state.pendingCommands, { id, tabId, command }],
    }));
    return id;
  },

  getPendingCommands: (tabId: string): PendingCommand[] => {
    return get().pendingCommands.filter((cmd) => cmd.tabId === tabId);
  },

  completeCommand: (commandId: string) => {
    set((state) => ({
      pendingCommands: state.pendingCommands.filter((cmd) => cmd.id !== commandId),
    }));
  },

  clearTabCommands: (tabId: string) => {
    set((state) => ({
      pendingCommands: state.pendingCommands.filter((cmd) => cmd.tabId !== tabId),
    }));
  },
}));
